import React, { useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { PipelineTiles } from '@/components/contracting/PipelineTiles';
import { RecentRequestsStrip } from '@/components/contracting/RecentRequestsStrip';

/**
 * AgentContractingHealth — second Command Center band on the Agent
 * dashboard. Mirrors the KR / Agency ContractingHealth pattern (Phases
 * 20.4 & 20.5), scoped to the logged-in agent's own requests and
 * appointments. Reuses the SAME `PipelineTiles` + `RecentRequestsStrip`
 * primitives so agent pipeline counts reconcile with the Agent Profile
 * Contracting Pipeline and /requests page.
 *
 * Inputs are already pre-loaded by `AgentDashboard` via `agentMeAPI.getProfile()`
 * — no additional fetches from this component.
 *
 * Empty-state chain:
 *   • parentLoading  → loading placeholder
 *   • requests + appointments both empty  → `agent-contracting-health-empty`
 *   • requests present but recent slice empty → `agent-contracting-health-no-recent`
 */

const RECENT_LIMIT = 5;

const sortByRecency = (a, b) => {
  const at = new Date(a?.updated_at || a?.created_at || 0).getTime();
  const bt = new Date(b?.updated_at || b?.created_at || 0).getTime();
  return bt - at;
};

export const AgentContractingHealth = ({
  requests = [],
  appointments = [],
  parentLoading = false,
}) => {
  const recentRequests = useMemo(
    () => [...requests].sort(sortByRecency).slice(0, RECENT_LIMIT),
    [requests],
  );

  const isEmpty = !parentLoading && requests.length === 0 && appointments.length === 0;

  return (
    <section className="mb-8" data-testid="agent-contracting-health">
      <div className="mb-3">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">
          Contracting Health
        </h2>
        <p className="text-sm text-slate-500 mt-1">
          Your contract requests, approvals, and active writing numbers.
        </p>
      </div>

      <Card className="animate-slideIn">
        <CardContent className="p-5">
          {parentLoading ? (
            <div className="py-8 text-center text-sm text-slate-400" data-testid="agent-contracting-health-loading">
              Loading contracting health…
            </div>
          ) : isEmpty ? (
            <div className="py-8 text-center" data-testid="agent-contracting-health-empty">
              <p className="text-sm font-medium text-slate-800">No contracting activity yet</p>
              <p className="text-xs text-slate-500 mt-1">
                Your first contract requests will appear here once submitted.
              </p>
            </div>
          ) : (
            <>
              <PipelineTiles
                requests={requests}
                appointments={appointments}
                testIdPrefix="agent-pipeline"
              />
              {recentRequests.length === 0 ? (
                <div
                  className="mt-5 pt-4 border-t text-center py-4"
                  style={{ borderColor: 'var(--kr-border)' }}
                  data-testid="agent-contracting-health-no-recent"
                >
                  <p className="text-sm text-slate-500">No recent contract activity</p>
                </div>
              ) : (
                <RecentRequestsStrip
                  requests={recentRequests}
                  maxItems={RECENT_LIMIT}
                  testIdPrefix="agent-recent-request"
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </section>
  );
};

export default AgentContractingHealth;
